import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { AssignRiderDialog } from "@/app/private/orders/assign-rider-dialog";
import { CancelDeliveryDialog } from "@/app/private/orders/cancel-delivery-dialog";
import { ManualCompleteDeliveryDialog } from "@/app/private/orders/manual-complete-delivery-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrderEvents } from "@/hooks/use-order-events";
import {
  type DeliveryState,
  type OrderStatus,
  type OrderWithItems,
  useOrders,
  useTransitionOrder,
} from "@/hooks/use-orders";
import { useActiveOrganization } from "@/hooks/use-organizations";
import { formatPrice } from "@/lib/format-price";
import { queryKeys } from "@/lib/query-keys";

const COLUMNS: { status: OrderStatus; key: string }[] = [
  { status: "pending", key: "pending" },
  { status: "accepted", key: "accepted" },
  { status: "in_progress", key: "in_progress" },
  { status: "ready", key: "ready" },
];

// Mirrors the backend DELIVERY_DELIVERED_VISIBILITY_MS in apps/api/server/utils/orders.ts.
// Used as a defensive client-side check; the backend filters out older delivered-recent orders.
const DELIVERY_DELIVERED_VISIBILITY_MS = 10 * 60 * 1000;

export default function OrdersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: org } = useActiveOrganization();
  const { data, error, isLoading } = useOrders({
    status: ["pending", "accepted", "in_progress", "ready"],
    includeDeliveryBoard: true,
    limit: 100,
  });

  const accept = useTransitionOrder("accept");
  const start = useTransitionOrder("start");
  const ready = useTransitionOrder("ready");
  const deliver = useTransitionOrder("deliver");
  const cancel = useTransitionOrder("cancel");
  const reject = useTransitionOrder("reject");
  const markPaid = useTransitionOrder("mark-paid");

  const [assignTarget, setAssignTarget] = useState<OrderWithItems | null>(null);
  const [cancelDeliveryTarget, setCancelDeliveryTarget] = useState<OrderWithItems | null>(null);
  const [manualCompleteTarget, setManualCompleteTarget] = useState<OrderWithItems | null>(null);

  useOrderEvents({ type: "org" }, () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
  });

  if (isLoading || !org) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-destructive">{error.message}</div>;
  }

  const orders = data?.orders ?? [];
  const ordersByStatus = new Map<OrderStatus, OrderWithItems[]>();
  const enCamino: OrderWithItems[] = [];
  const now = Date.now();
  for (const o of orders) {
    if (o.fulfillmentType === "delivery" && o.deliverySummary) {
      const state = o.deliverySummary.state;
      if (state === "en_route" || state === "arrived") {
        enCamino.push(o);
        continue;
      }
      if (state === "delivered" && o.deliverySummary.deliveredAt) {
        const deliveredAtMs = new Date(o.deliverySummary.deliveredAt).getTime();
        if (now - deliveredAtMs <= DELIVERY_DELIVERED_VISIBILITY_MS) {
          enCamino.push(o);
          continue;
        }
      }
    }
    const list = ordersByStatus.get(o.status) ?? [];
    list.push(o);
    ordersByStatus.set(o.status, list);
  }
  enCamino.sort((a, b) => {
    const aActive =
      a.deliverySummary?.state === "en_route" || a.deliverySummary?.state === "arrived";
    const bActive =
      b.deliverySummary?.state === "en_route" || b.deliverySummary?.state === "arrived";
    if (aActive !== bActive) {
      return aActive ? -1 : 1;
    }
    if (aActive && bActive) {
      const aTime = new Date(
        a.deliverySummary?.arrivedAt ?? a.deliverySummary?.enRouteAt ?? "",
      ).getTime();
      const bTime = new Date(
        b.deliverySummary?.arrivedAt ?? b.deliverySummary?.enRouteAt ?? "",
      ).getTime();
      return aTime - bTime;
    }
    const aDelivered = new Date(a.deliverySummary?.deliveredAt ?? "").getTime();
    const bDelivered = new Date(b.deliverySummary?.deliveredAt ?? "").getTime();
    return bDelivered - aDelivered;
  });

  const handle = async (
    mutation:
      | typeof accept
      | typeof start
      | typeof ready
      | typeof deliver
      | typeof cancel
      | typeof reject
      | typeof markPaid,
    orderId: string,
    reason?: string,
  ) => {
    try {
      await mutation.mutateAsync({ orderId, reason });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    }
  };

  const getOrderContext = (order: OrderWithItems) => {
    if (order.origin === "public_catalog") {
      return {
        label: t("orders.origin.publicCatalog", {
          defaultValue: "Catálogo público",
        }),
        variant: "secondary" as const,
      };
    }
    if (order.restaurantTable?.label) {
      return {
        label: order.restaurantTable.label,
        variant: "outline" as const,
      };
    }
    if (order.restaurantTableId) {
      return {
        label: t("orders.table.fallback", { defaultValue: "Mesa" }),
        variant: "outline" as const,
      };
    }
    return null;
  };

  const getDeliveryBadge = (
    order: OrderWithItems,
  ): { label: string; variant: "outline" | "secondary" | "default" | "destructive" } | null => {
    if (order.fulfillmentType !== "delivery") {
      return null;
    }

    const summary = order.deliverySummary;
    const riderName = summary?.riderName ?? "";
    const state: DeliveryState = summary?.state ?? "unassigned";

    let label: string;
    switch (state) {
      case "unassigned":
        label = t("orders.delivery.unassigned", { defaultValue: "Sin rider" });
        return { label, variant: "outline" };
      case "assigned":
        label = t("orders.delivery.assignedTo", {
          defaultValue: "Asignado a {{name}}",
          name: riderName,
        });
        return { label, variant: "secondary" };
      case "accepted":
        label = t("orders.delivery.acceptedBy", {
          defaultValue: "Aceptado por {{name}}",
          name: riderName,
        });
        return { label, variant: "secondary" };
      case "en_route":
        label = t("orders.delivery.enRouteWith", {
          defaultValue: "En camino con {{name}}",
          name: riderName,
        });
        return { label, variant: "default" };
      case "arrived":
        label = t("orders.delivery.arrivedWith", {
          defaultValue: "Llegó con {{name}}",
          name: riderName,
        });
        return { label, variant: "default" };
      case "delivered":
        label = t("orders.delivery.deliveredBy", {
          defaultValue: "Entregado por {{name}}",
          name: riderName,
        });
        return { label, variant: "secondary" };
      case "failed":
        label = t("orders.delivery.failed", {
          defaultValue: "No entregado",
        });
        return { label, variant: "destructive" };
      case "cancelled":
        label = t("orders.delivery.cancelled", {
          defaultValue: "Cancelado",
        });
        return { label, variant: "destructive" };
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h1 className="font-semibold text-2xl">
          {t("orders.dashboard.title", { defaultValue: "Pedidos" })}
        </h1>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/menu/tables">{t("orders.dashboard.tables", { defaultValue: "Mesas" })}</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/orders/settings">
              {t("orders.dashboard.settings", {
                defaultValue: "Configuración",
              })}
            </Link>
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        {COLUMNS.map(({ status, key }) => {
          const list = ordersByStatus.get(status) ?? [];
          return (
            <Column
              emptyLabel={t("orders.dashboard.empty", { defaultValue: "Vacío" })}
              key={key}
              list={list}
              renderCard={(order) => {
                const context = getOrderContext(order);
                const deliveryBadge = getDeliveryBadge(order);
                return (
                  <Card key={order.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-base">
                        <span>#{order.dailyNumber}</span>
                        <span className="font-mono text-muted-foreground text-xs">
                          {formatPrice(order.total, order.currency)}
                        </span>
                      </CardTitle>
                      {context ? <Badge variant={context.variant}>{context.label}</Badge> : null}
                      {deliveryBadge ? (
                        <Badge variant={deliveryBadge.variant}>{deliveryBadge.label}</Badge>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-1 text-xs">
                        <Badge variant="outline">
                          {t(`orders.payment.${order.paymentStatus}`, {
                            defaultValue: order.paymentStatus,
                          })}
                        </Badge>
                        {order.paymentMode === "pay_later" ? (
                          <Badge variant="default">
                            {t("orders.payment.payAtCounter", {
                              defaultValue: "Pago al final",
                            })}
                          </Badge>
                        ) : null}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <ul className="space-y-1 text-sm">
                        {order.items.map((item) => (
                          <li key={item.id}>
                            {item.quantity}× {item.nameSnapshot}
                          </li>
                        ))}
                      </ul>
                      {order.notes ? (
                        <p className="rounded bg-muted p-2 text-xs">{order.notes}</p>
                      ) : null}
                      {order.origin === "public_catalog" && order.contactName ? (
                        <p className="text-muted-foreground text-xs">{order.contactName}</p>
                      ) : null}
                      <div className="flex flex-wrap gap-1 pt-1">
                        {status === "pending" ? (
                          <>
                            <Button
                              onClick={() => handle(accept, order.id)}
                              size="sm"
                              type="button"
                            >
                              {t("orders.actions.accept", {
                                defaultValue: "Aceptar",
                              })}
                            </Button>
                            <Button
                              onClick={() => handle(reject, order.id, "Rechazado")}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              {t("orders.actions.reject", {
                                defaultValue: "Rechazar",
                              })}
                            </Button>
                          </>
                        ) : null}
                        {status === "accepted" ? (
                          <Button onClick={() => handle(start, order.id)} size="sm" type="button">
                            {t("orders.actions.start", {
                              defaultValue: "En preparación",
                            })}
                          </Button>
                        ) : null}
                        {status === "accepted" || status === "in_progress" ? (
                          <Button onClick={() => handle(ready, order.id)} size="sm" type="button">
                            {t("orders.actions.ready", {
                              defaultValue: "Listo",
                            })}
                          </Button>
                        ) : null}
                        {status === "ready" && order.fulfillmentType !== "delivery" ? (
                          <Button onClick={() => handle(deliver, order.id)} size="sm" type="button">
                            {t("orders.actions.deliver", {
                              defaultValue: "Entregado",
                            })}
                          </Button>
                        ) : null}
                        {status !== "ready" && status !== "pending" ? (
                          <Button
                            onClick={() => handle(cancel, order.id)}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            {t("orders.actions.cancel", {
                              defaultValue: "Cancelar",
                            })}
                          </Button>
                        ) : null}
                        {order.paymentMode === "pay_later" && order.paymentStatus !== "paid" ? (
                          <Button
                            onClick={() => handle(markPaid, order.id)}
                            size="sm"
                            type="button"
                            variant="secondary"
                          >
                            {t("orders.actions.markPaid", {
                              defaultValue: "Marcar pagado",
                            })}
                          </Button>
                        ) : null}
                        <Button asChild size="sm" type="button" variant="ghost">
                          <Link to={`/orders/${order.id}`}>
                            {t("orders.actions.view", {
                              defaultValue: "Ver",
                            })}
                          </Link>
                        </Button>
                        {order.fulfillmentType === "delivery" &&
                        canAssignOrReassign(order.deliverySummary) ? (
                          <Button
                            data-testid={`assign-rider-${order.id}`}
                            onClick={() => setAssignTarget(order)}
                            size="sm"
                            type="button"
                            variant="secondary"
                          >
                            {order.deliverySummary?.riderId
                              ? t("orders.actions.reassignRider", {
                                  defaultValue: "Reasignar rider",
                                })
                              : t("orders.actions.assignRider", {
                                  defaultValue: "Asignar rider",
                                })}
                          </Button>
                        ) : null}
                        {status === "ready" && order.fulfillmentType === "delivery" ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                aria-label={t("orders.actions.moreActions", {
                                  defaultValue: "Más acciones",
                                })}
                                data-testid={`order-actions-${order.id}`}
                                size="sm"
                                type="button"
                                variant="ghost"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                data-testid={`manual-complete-${order.id}`}
                                onSelect={() => setManualCompleteTarget(order)}
                              >
                                {t("orders.actions.manualCompleteDelivery", {
                                  defaultValue: "Marcar como entregado",
                                })}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                );
              }}
              title={t(`orders.status.${key}`, { defaultValue: key })}
            />
          );
        })}
        <Column
          emptyLabel={t("orders.dashboard.empty", { defaultValue: "Vacío" })}
          key="en_camino"
          list={enCamino}
          renderCard={(order) => {
            const context = getOrderContext(order);
            const deliveryBadge = getDeliveryBadge(order);
            return (
              <Card key={order.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>#{order.dailyNumber}</span>
                    <span className="font-mono text-muted-foreground text-xs">
                      {formatPrice(order.total, order.currency)}
                    </span>
                  </CardTitle>
                  {context ? <Badge variant={context.variant}>{context.label}</Badge> : null}
                  {deliveryBadge ? (
                    <Badge variant={deliveryBadge.variant}>{deliveryBadge.label}</Badge>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    <Badge variant="outline">
                      {t(`orders.payment.${order.paymentStatus}`, {
                        defaultValue: order.paymentStatus,
                      })}
                    </Badge>
                    {order.paymentMode === "pay_later" ? (
                      <Badge variant="default">
                        {t("orders.payment.payAtCounter", {
                          defaultValue: "Pago al final",
                        })}
                      </Badge>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <ul className="space-y-1 text-sm">
                    {order.items.map((item) => (
                      <li key={item.id}>
                        {item.quantity}× {item.nameSnapshot}
                      </li>
                    ))}
                  </ul>
                  {order.notes ? (
                    <p className="rounded bg-muted p-2 text-xs">{order.notes}</p>
                  ) : null}
                  {order.origin === "public_catalog" && order.contactName ? (
                    <p className="text-muted-foreground text-xs">{order.contactName}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-1 pt-1">
                    <Button asChild size="sm" type="button" variant="ghost">
                      <Link to={`/orders/${order.id}`}>
                        {t("orders.actions.view", {
                          defaultValue: "Ver",
                        })}
                      </Link>
                    </Button>
                    {order.deliverySummary?.id && order.deliverySummary.state === "en_route" ? (
                      <Button
                        data-testid={`cancel-delivery-${order.id}`}
                        onClick={() => setCancelDeliveryTarget(order)}
                        size="sm"
                        type="button"
                        variant="destructive"
                      >
                        {t("orders.actions.cancelDelivery", {
                          defaultValue: "Cancelar delivery",
                        })}
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          }}
          title={t("orders.status.en_camino", { defaultValue: "En camino" })}
        />
      </div>
      {assignTarget ? (
        <AssignRiderDialog
          deliverySummary={assignTarget.deliverySummary}
          onAssigned={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
          }}
          onOpenChange={(open) => {
            if (!open) {
              setAssignTarget(null);
            }
          }}
          open
          orderDailyNumber={assignTarget.dailyNumber}
          orderId={assignTarget.id}
        />
      ) : null}
      {cancelDeliveryTarget && cancelDeliveryTarget.deliverySummary?.id ? (
        <CancelDeliveryDialog
          deliveryId={cancelDeliveryTarget.deliverySummary.id}
          deliveryState={cancelDeliveryTarget.deliverySummary.state}
          onCancelled={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
          }}
          onOpenChange={(open) => {
            if (!open) {
              setCancelDeliveryTarget(null);
            }
          }}
          open
          orderDailyNumber={cancelDeliveryTarget.dailyNumber}
          orderId={cancelDeliveryTarget.id}
          riderName={cancelDeliveryTarget.deliverySummary.riderName}
        />
      ) : null}
      {manualCompleteTarget ? (
        <ManualCompleteDeliveryDialog
          onCompleted={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
          }}
          onOpenChange={(open) => {
            if (!open) {
              setManualCompleteTarget(null);
            }
          }}
          open
          orderDailyNumber={manualCompleteTarget.dailyNumber}
          orderId={manualCompleteTarget.id}
          riderName={manualCompleteTarget.deliverySummary?.riderName ?? null}
        />
      ) : null}
    </div>
  );
}

function canAssignOrReassign(summary: OrderWithItems["deliverySummary"]): boolean {
  if (!summary) {
    return true;
  }
  const blocked = new Set<DeliveryState>(["en_route", "arrived", "delivered", "cancelled"]);
  return !blocked.has(summary.state);
}

function Column({
  title,
  list,
  renderCard,
  emptyLabel,
}: {
  title: string;
  list: OrderWithItems[];
  renderCard: (order: OrderWithItems) => React.ReactNode;
  emptyLabel: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-sm uppercase tracking-wide">{title}</h2>
        <Badge variant="secondary">{list.length}</Badge>
      </div>
      <div className="space-y-2">
        {list.map((order) => renderCard(order))}
        {list.length === 0 ? (
          <p className="rounded border border-border border-dashed p-4 text-center text-muted-foreground text-sm">
            {emptyLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
}
